import {
  createQuery,
  keepPreviousData,
  type CreateQueryOptions,
} from '@tanstack/svelte-query';
import type {
  StreakResponse,
  DateCell,
  Period,
  StarDistribution,
  DailyStats,
  PremiumResponse,
  TwistPuzzle,
  Puzzle,
  DailyPuzzle,
} from '~/types';
import { dateToString, getTodayUTC, getTimeTilNextPuzzle } from '~/utils/dates';
import { filterParams } from '~/auth/utils';
import { page } from '$app/state';
import type {
  DailyVariant,
  GameMode,
  TwistVariant,
} from '~/store/game.svelte';
import { DailyGame } from '~/store/daily.svelte'
import type { TwistGame } from '~/store/twist.svelte.js'
import { getPremium } from './premium.remote.js';
import { getDailyStreak } from './analytics.remote.js';
import { getDailyPuzzle, getDailyCalendar } from './puzzles.remote.js';
import { getDailyStats } from './stats.remote.js';
import { getClassicSnapshot } from './snapshots.remote.js';
import { freshQuery } from '~/utils/freshQuery';

export const premiumQueryKey = () => ['auth', 'premium'];

export const isPremium = () =>
  createQuery(() => ({
    queryKey: premiumQueryKey(),
    queryFn: () => freshQuery(getPremium()),
    enabled: Boolean(page.data.user),
    initialData: { premium: Boolean(page.data.isPremium) } as PremiumResponse,
    select: (data) => Boolean(data?.premium),
  }));

export const getDailyMode = (mode: GameMode): DailyVariant =>
  mode === 'daily' ? '6x6' : '5x5';

export type StatsMode = 'first' | 'best' | 'global';

type DailyStatsArgs = {
  gameId: string;
  mode: DailyVariant;
  stat: StatsMode;
};

export const getDailyStatsQueryKey = (args: DailyStatsArgs) => [
  'premium',
  'daily',
  'stats',
  JSON.stringify(args),
];

export const dailyChartQuery = (args: DailyStatsArgs) =>
  createQuery<StarDistribution[]>(() => ({
    queryKey: getDailyStatsQueryKey(args),
    queryFn: () =>
      freshQuery(getDailyStats({
        mode: args.mode,
        stat: args.stat,
        puzzleId: args.gameId,
      })),
    refetchOnMount: true,
    staleTime:
      args.stat === 'global' ? 1000 * 60 * 1 : Number.POSITIVE_INFINITY, // 1m
    enabled: true, // TODO premium,
    placeholderData: keepPreviousData,
  }));

export const getChart = (stars: number[]) =>
  Array.from({ length: 5 }).map((_, i) => ({
    total: isNaN(stars[i]) ? 0 : stars[i],
    true: isNaN(stars[i]) ? 0 : stars[i],
    false: 0,
  }));

export const getClassicRankKey = (period: Period) => [
  'classic',
  'rank',
  period,
];

// TODO
export const getClassicRankQuery = (period: Period) =>
  createQuery(() => ({
    queryKey: getClassicRankKey(period),
    queryFn: (): number => -1,
  }));

export type { ClassicSnapshotMeta } from './snapshots.remote.js';

export const classicSnapshotQueryKey = () => ['games', 'classic', 'snapshots'];

export const classicSnapshotQuery = () =>
  createQuery(() => ({
    queryKey: classicSnapshotQueryKey(),
    queryFn: () => freshQuery(getClassicSnapshot()),
    enabled: Boolean(page.data.user),
    refetchOnMount: true,
    staleTime: 0,
  }));

export const getStreakQueryKey = (mode: DailyVariant) => [
  'premium',
  'daily',
  mode,
  'streak',
];

type WinStreakArgs = {
  mode: DailyVariant;
  enabled: boolean;
};

export const winStreakQuery = (args: WinStreakArgs) =>
  createQuery(() => ({
    queryKey: getStreakQueryKey(args.mode),
    queryFn: (): Promise<StreakResponse> => freshQuery(getDailyStreak(args.mode)),
    select: (data: StreakResponse) => ({
      currStreak: data.current_streak,
      bestStreak: data.best_streak,
      winRate: data.won_games / data.total_games,
    }),
    refetchOnMount: true,
    enabled: Boolean(args.enabled),
    placeholderData: keepPreviousData,
  }));

export const getStreak = ({ stars, currStreak, bestStreak }: DailyStats) => {
  const gamesPlayed =
    stars && Object.keys(stars).length
      ? Object.values(stars).reduce((acc, val) => acc + (val ?? 0), 0)
      : 0;
  const winRate = stars ? (gamesPlayed - (stars[0] ?? 0)) / gamesPlayed : 0;

  return {
    bestStreak,
    currStreak,
    winRate,
  };
};

type PuzzleArgs = {
  date?: string | null | false;
  mode?: string | null | false;
  variant?: string | null | false;
  key?: string | null | false;
};

type Page = {
  params: Record<string, string | undefined>;
  url: { searchParams: URLSearchParams };
};

// TODO rename mode -> variant in URL?
export const stringifyPuzzleKey = (args: PuzzleArgs = {}) =>
  new URLSearchParams(
    filterParams({ ...args, mode: args.variant ?? args.mode }),
  ).toString();

// TODO
// structural so both page (from $app/state) and load events fit
type PuzzleUrlSource = {
  params: { mode?: string };
  url: { searchParams: { get(name: string): string | null } };
};

export const getPuzzleKeyFromURL = (page: PuzzleUrlSource) =>
  stringifyPuzzleKey({
    mode: page.params.mode === 'daily' ? '6x6' : '5x5',
    date: page.url.searchParams.get('date') || 'latest',
    key: page.url.searchParams.get('puzzle'),
  });

export const getTwistPuzzleKeyFromURL = (page: PuzzleUrlSource) =>
  stringifyPuzzleKey({
    mode: '6x6',
    date: page.url.searchParams.get('date') || 'latest',
    key: page.url.searchParams.get('puzzle'),
  });

// TODO lump this in with other logic?
export const getLatestPuzzleKey = (variant: DailyVariant) =>
  'date=latest&mode=' + variant;

export const puzzleQuery = <T extends Puzzle>(search: string) =>
  createQuery<T, unknown, T>(() => ({
    queryKey: ['daily', search],
    queryFn: () => {
      const params = new URLSearchParams(search);
      return freshQuery(getDailyPuzzle({
        mode: params.get('mode'),
        date: params.get('date'),
        puzzle: params.get('puzzle'),
      })) as unknown as Promise<T>;
      // TODO migrate backend?
      // puzzle.mode = 'daily';
    },
  }));

export const twistPuzzleQuery = (search: string) =>
  createQuery(
    () =>
      ({
        queryKey: ['twist', search],
        queryFn: async () => {
          const res = await fetch(`/api/puzzles/twist?${search}`);

          if (res.ok) {
            const game = await res.json();
            return game as TwistPuzzle;
          } else {
            throw res;
          }
        },
      }) satisfies CreateQueryOptions,
  );

export const latestDailyQueryOptions = (variant: DailyVariant) => {
  const search = getLatestPuzzleKey(variant);
  return {
    queryKey: ['daily', search],
    queryFn: () =>
      freshQuery(getDailyPuzzle({
        mode: variant,
        date: 'latest',
        puzzle: null,
      })) as unknown as Promise<DailyPuzzle>,
    // Once the player has completed the latest daily and its countdown has
    // elapsed, poll for the next puzzle. Client clock skew or a delayed publish
    // can make the timer expire before the server rolls over, so a single
    // refetch may return the same completed puzzle; polling retries until a
    // fresh puzzle arrives, then stops on its own.
    refetchInterval: (query: { state: { data?: DailyPuzzle } }) => {
      const data = query.state.data;
      return data?.completed && getTimeTilNextPuzzle(data.date) <= 0
        ? 10_000
        : false;
    },
    // keep polling for the next puzzle even when the tab is backgrounded
    refetchIntervalInBackground: true,
    // re-check for a new puzzle whenever the user returns to the page
    // ('always' is required since the global staleTime is Infinity)
    refetchOnWindowFocus: 'always' as const,
  };
};

// just call puzzle with no arguments to get latest
export const latestDailyQuery = (variant: DailyVariant) =>
  createQuery<DailyPuzzle, unknown, DailyPuzzle>(
    () => latestDailyQueryOptions(variant),
  );

export const latestTwistQuery = (variant: TwistVariant) =>
  twistPuzzleQuery(getLatestPuzzleKey(variant));

export const isLatestDaily = (game: DailyGame, latest?: Puzzle) =>
  game.id === latest?.id && !latest?.completed;

export const isLatestTwist = (game: TwistGame, latest?: TwistPuzzle) =>
  game.id === latest?.id && !latest?.completed;

export const getCalendarQueryKey = (
  mode: DailyVariant,
  month?: number,
  year?: number,
) => ['daily', mode, month, year];

export const calendarQuery = (
  mode: DailyVariant,
  date: { month: number; year: number },
) =>
  createQuery(() => ({
    queryKey: getCalendarQueryKey(mode, date.month, date.year),
    placeholderData: {},
    queryFn: async () => {
      const dateString = dateToString(new Date(date.year, date.month, 1));
      const data = await freshQuery(getDailyCalendar({ date: dateString, mode }));
      return Object.fromEntries(data.map((d) => [d.date, d]));
    },
    select: (data: Record<string, DateCell>) => {
      const today = getTodayUTC();
      let totalStars = 0;
      let receivedStars = 0;
      let totalGreenStars = 0;
      let receivedGreenStars = 0;
      const dates: DateCell[][] = [];

      const firstDay: number = new Date(date.year, date.month).getDay();
      const allDays: number =
        32 - new Date(date.year, date.month, 32).getDate();

      let day = 1;

      for (let i = 0; i < 6; i++) {
        const row: DateCell[] = [];
        for (let j = 0; j < 7; j++) {
          const d = new Date(date.year, date.month, day);
          const record = data[dateToString(d)] ?? {};
          const cell: DateCell = {
            id: record.id,
            date: dateToString(d),
            stars: record.stars,
            fullClear: record.fullClear,
            empty: false,
            disabled: false,
          };
          if (i === 0 && j < firstDay) {
            cell.empty = true;
            cell.disabled = true;
          } else if (day > allDays) {
            break;
          } else {
            receivedStars += cell.stars ?? 0;
            receivedGreenStars += cell.fullClear ? 1 : 0;
            totalStars += 3;
            totalGreenStars += 1;
            day++;
          }
          // outside valid range
          if (d > today || d < new Date(2022, 8, 10)) {
            cell.disabled = true;
          }
          row.push(cell);
        }
        dates.push(row);
      }
      // ensure constant height
      if (dates[dates.length - 1].length === 0) {
        dates[dates.length - 1] = [
          {
            id: '',
            date: new Date().toISOString(),
            stars: 0,
            fullClear: false,
            disabled: true,
            empty: true,
          },
        ];
      }
      return {
        dates,
        totalStars,
        receivedStars,
        totalGreenStars,
        receivedGreenStars,
      };
    },
    // refetchOnMount: 'always' as const,
  }));

type Definition = {
  phonetic?: string;
  sourceUrl: string;
  meanings: Array<{
    partOfSpeech: string;
    definitions: Array<{
      definition: string;
    }>;
  }>;
};

// https://en.wiktionary.org/api/rest_v1/#/Page%20content/get_page_definition__term_
type WiktionaryResponse = Record<
  string,
  Array<{
    partOfSpeech: string;
    language: string;
    definitions: Array<{ definition: string }>;
  }>
>;

// definitions come back as HTML (links, usage labels); render as plain text.
// Templates can inline <style> blocks, whose CSS textContent would otherwise
// leak into the definition. Nested <ol>/<ul> hold sub-senses, which the API
// also returns as their own definitions, so drop them to avoid duplicates.
const htmlToText = (html: string) => {
  const body = new DOMParser().parseFromString(html, 'text/html').body;
  body.querySelectorAll('style, script, link, ol, ul').forEach((el) => el.remove());
  return (body.textContent ?? '').replace(/\s+/g, ' ').trim();
};

const wikimediaHeaders = { 'Api-User-Agent': 'GramJam (https://gramjam.app)' };

// The definition endpoint has no pronunciations, so read the IPA templates
// ({{IPA|en|/ˈkɑɹp/|a=GA}}) from the English section of the page wikitext.
// Prefers General American, then the first listed accent.
const fetchPhonetic = async (term: string) => {
  const params = new URLSearchParams({
    action: 'parse',
    format: 'json',
    formatversion: '2',
    origin: '*',
    prop: 'wikitext',
    page: term,
  });
  const response = await fetch(`https://en.wiktionary.org/w/api.php?${params}`, {
    headers: wikimediaHeaders,
  });
  if (!response.ok) return;
  const data: { parse?: { wikitext: string } } = await response.json();
  const wikitext = data.parse?.wikitext ?? '';
  const start = wikitext.search(/^==English==\s*$/m);
  if (start < 0) return;
  // the English section runs until the next language (level 2) heading
  const rest = wikitext.slice(start + 1);
  const end = rest.search(/^==[^=]/m);
  const english = end < 0 ? rest : rest.slice(0, end);

  const templates = [...english.matchAll(/\{\{IPA\|en\|([^}]*)\}\}/g)].map((m) => {
    const params = m[1].split('|');
    return {
      accents: params.find((p) => p.startsWith('a='))?.slice(2) ?? '',
      transcriptions: params.filter((p) => /^[/[]/.test(p)),
    };
  });
  const preferred =
    templates.find((t) => /\b(GA|US)\b/.test(t.accents)) ?? templates[0];
  // phonemic /…/ over narrow phonetic […]
  return (
    preferred?.transcriptions.find((p) => p.startsWith('/')) ??
    preferred?.transcriptions[0]
  );
};

export const lookupWordQuery = (word?: string | null) =>
  createQuery(() => ({
    // bump when parsing changes so entries persisted by older parsers aren't reused
    queryKey: ['definition', 'wiktionary-v4', word],
    queryFn: async (): Promise<Definition | undefined> => {
      if (!word) return;
      const term = encodeURIComponent(word.toLowerCase());
      const [response, phonetic] = await Promise.all([
        fetch(`https://en.wiktionary.org/api/rest_v1/page/definition/${term}`, {
          headers: wikimediaHeaders,
        }),
        // optional, never fail the definition over it
        fetchPhonetic(word.toLowerCase()).catch(() => undefined),
      ]);
      if (!response.ok) throw Error('no definitions found');
      const data: WiktionaryResponse = await response.json();
      // the `en` key also holds Translingual entries (symbols, codes)
      const meanings = (data.en ?? [])
        .filter((entry) => entry.language === 'English')
        .map((entry) => ({
          partOfSpeech: entry.partOfSpeech.toLowerCase(),
          definitions: entry.definitions
            .map((d) => ({ definition: htmlToText(d.definition) }))
            .filter((d) => d.definition),
        }))
        .filter((meaning) => meaning.definitions.length > 0);
      if (meanings.length === 0) throw Error('no definitions found');
      return {
        phonetic,
        sourceUrl: `https://en.wiktionary.org/wiki/${term}`,
        meanings,
      };
    },
    retry: false,
    enabled: Boolean(word),
    staleTime: 1000 * 60 * 5, // 5 min
  }));

export type Pronunciation = {
  label: string;
  src: string;
};

// English recordings are named En-us-*, En-uk-*, en-au-*, ... or
// LL-Q1860 (eng)-* (Lingua Libre); the page also lists other languages
const englishAudio = /^File:(en-(?:(us|uk|gb|au|ca|nz|ie)-)?|LL-Q1860[ _]\(eng\)-)/i;
const accentLabels: Record<string, string> = { gb: 'UK' };
const accentOrder = ['US', 'UK', 'AU', 'CA', 'NZ', 'IE', ''];

const accentLabel = (title: string) => {
  const accent = title.match(englishAudio)?.[2]?.toLowerCase() ?? '';
  return accentLabels[accent] ?? accent.toUpperCase();
};

// fetched only once the user asks to hear the word
export const pronunciationQuery = (word: string | null | undefined, requested: boolean) =>
  createQuery(() => ({
    queryKey: ['pronunciation', 'v1', word],
    queryFn: async (): Promise<Pronunciation[]> => {
      if (!word) return [];
      const term = encodeURIComponent(word.toLowerCase());
      const mediaResponse = await fetch(
        `https://en.wiktionary.org/api/rest_v1/page/media-list/${term}`,
        { headers: wikimediaHeaders },
      );
      if (!mediaResponse.ok) return [];
      const media: { items: Array<{ type: string; title: string }> } =
        await mediaResponse.json();

      // one recording per accent, most familiar accents first
      const byAccent = new Map<string, string>();
      media.items
        .filter((item) => item.type === 'audio' && englishAudio.test(item.title))
        .map((item) => ({ title: item.title, label: accentLabel(item.title) }))
        .sort((a, b) => accentOrder.indexOf(a.label) - accentOrder.indexOf(b.label))
        .forEach(({ title, label }) => {
          if (!byAccent.has(label)) byAccent.set(label, title);
        });
      if (byAccent.size === 0) return [];

      // Commons transcodes every recording to mp3; Safari can't play ogg
      const params = new URLSearchParams({
        action: 'query',
        format: 'json',
        formatversion: '2',
        origin: '*',
        prop: 'videoinfo',
        viprop: 'url|derivatives',
        titles: [...byAccent.values()].join('|'),
      });
      const infoResponse = await fetch(
        `https://commons.wikimedia.org/w/api.php?${params}`,
        { headers: wikimediaHeaders },
      );
      if (!infoResponse.ok) return [];
      const info: {
        query: {
          normalized?: Array<{ from: string; to: string }>;
          pages: Array<{
            title: string;
            videoinfo?: Array<{ url: string; derivatives?: Array<{ type: string; src: string }> }>;
          }>;
        };
      } = await infoResponse.json();

      // titles come back normalized (underscores -> spaces)
      const normalized = new Map(info.query.normalized?.map((n) => [n.to, n.from]));
      const srcByTitle = new Map(
        info.query.pages.flatMap((p) => {
          const vi = p.videoinfo?.[0];
          if (!vi) return [];
          const mp3 = vi.derivatives?.find((d) => d.type === 'audio/mpeg')?.src;
          return [[normalized.get(p.title) ?? p.title, mp3 ?? vi.url] as const];
        }),
      );
      return [...byAccent].flatMap(([label, title]) => {
        const src = srcByTitle.get(title);
        return src ? [{ label, src }] : [];
      });
    },
    retry: false,
    enabled: Boolean(word) && requested,
    staleTime: Infinity,
  }));
